import { useState, useEffect } from "react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import LanguageToggle from "@/components/LanguageToggle";
import LegalFooter from "@/components/LegalFooter";

export default function Register() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ clubName: "", name: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const plan = params.get("plan") || "";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.clubName.trim()) { setError("Enter your club name"); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await register({ clubName: form.clubName.trim(), name: form.name, email: form.email, password: form.password, plan });
      toast.success("Welcome! Your 14-day free trial has started.");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="fixed top-4 right-4 z-50"><LanguageToggle /></div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center font-display font-black uppercase tracking-tight text-3xl">Technical<span className="text-primary">Hub</span></div>
        <p className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground mb-2">Technical Hub · create your club</p>
        <h2 className="font-display font-black uppercase tracking-tight text-4xl mb-2">Start free trial</h2>
        <p className="text-sm text-muted-foreground mb-8">14 days free. No credit card required to start.</p>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2"><Label htmlFor="club">Club name</Label><Input id="club" data-testid="register-club-input" value={form.clubName} onChange={set("clubName")} placeholder="e.g. Demo United FC" required /></div>
          <div className="space-y-2"><Label htmlFor="name">Your name</Label><Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Name Surname" /></div>
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" data-testid="register-email-input" value={form.email} onChange={set("email")} placeholder="name@club.com" required /></div>
          <div className="space-y-2"><Label htmlFor="password">Password (min 8 characters)</Label><Input id="password" type="password" data-testid="register-password-input" value={form.password} onChange={set("password")} required minLength={8} /></div>
          <div className="space-y-2"><Label htmlFor="confirm">Confirm password</Label><Input id="confirm" type="password" data-testid="register-confirm-input" value={form.confirm} onChange={set("confirm")} required /></div>
          {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
          <Button type="submit" data-testid="register-submit-button" disabled={loading} className="w-full font-display font-bold uppercase tracking-wide text-base h-11">{loading ? "Creating…" : "Create club & start trial"}</Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6">Already have an account? <Link to="/login" data-testid="go-login" className="text-primary font-bold hover:underline">Sign in</Link></p>
        <p className="text-sm text-muted-foreground mt-2">See plans? <Link to="/pricing" data-testid="go-pricing" className="text-primary font-bold hover:underline">Pricing</Link></p>
        <LegalFooter />
      </div>
    </div>
  );
}
